import { EnhancedEventEmitter } from '../EnhancedEventEmitter';
import { Message } from '../Message';
import { Logger } from '../Logger';
import WebSocketTransport from './WebSocketTransport';

const logger = new Logger('WebTransportTransport');
const DEFAULT_MAX_RETRY_TIME = 120 * 1000;
const HEARTBEAT_INTERVAL = 10 * 1000;
const HEARTBEAT_CHECK_INTERVAL = 5 * 1000;
const HEARTBEAT_MAX_MISSED = 6;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const getRuntime = () => (typeof window !== 'undefined' ? window : {});

const toWebTransportUrl = (url, path = '/webtransport/') => {
    const source = new URL(url);
    const runtime = getRuntime();
    const next =
        runtime.location?.protocol === 'https:'
            ? new URL(`${runtime.location.protocol}//${runtime.location.host}`)
            : new URL(url);

    next.protocol = next.protocol === 'wss:' ? 'https:' : next.protocol;
    next.protocol = next.protocol === 'ws:' ? 'http:' : next.protocol;
    next.pathname = path;
    next.search = source.search;
    return next.toString();
};

const toConfiguredWebTransportUrl = (quicUrl, signalUrl) => {
    const source = new URL(signalUrl);
    const runtime = getRuntime();
    const base =
        runtime.location?.protocol === 'https:'
            ? `${runtime.location.protocol}//${runtime.location.host}`
            : source.origin;
    const next = new URL(quicUrl, base);

    if (!next.search) next.search = source.search;
    return next.toString();
};

const toServerCertificateHashes = (hash) => {
    if (!hash) return undefined;
    const bytes = Uint8Array.from(atob(hash), (char) => char.charCodeAt(0));
    return [{ algorithm: 'sha-256', value: bytes }];
};

export default class WebTransportTransport extends EnhancedEventEmitter {
    constructor(url, options = {}) {
        super();

        logger.debug('constructor() [url:%s, options:%o]', url, options);

        this._closed = false;
        this._url = url;
        this._options = options;
        this._transport = null;
        this._reader = null;
        this._writer = null;
        this._fallbackTransport = null;
        this._connectStartedAt = Date.now();
        this._maxRetryTime = options.maxRetryTime || DEFAULT_MAX_RETRY_TIME;
        this._buffer = '';
        this._encoder = new TextEncoder();
        this._decoder = new TextDecoder();
        this._heartbeatTimer = null;
        this._heartbeatCheckTimer = null;
        this._missedPongs = 0;

        this._runWebTransport();
    }

    get closed() {
        return this._closed;
    }

    close() {
        if (this._closed) return;
        this._closed = true;

        if (this._fallbackTransport) {
            this._fallbackTransport.close();
            return;
        }

        this.safeEmit('close');
        this._closeWebTransport();
    }

    disconnect() {
        if (this._fallbackTransport) {
            this._fallbackTransport.disconnect();
            return;
        }

        this._closeWebTransport();
        if (!this._closed) {
            this.safeEmit('disconnected');
            this._runWebTransport();
        }
    }

    async send(message) {
        if (this._fallbackTransport) {
            return this._fallbackTransport.send(message);
        }
        if (this._closed) return;
        if (!this._writer) throw new Error('webtransport not ready');

        const payload = `${JSON.stringify(message)}\n`;
        await this._writer.write(this._encoder.encode(payload));
    }

    async _runWebTransport() {
        if (this._closed) return;

        const runtime = getRuntime();
        if (!runtime.WebTransport) {
            this._fallbackToWebSocket('WebTransport API is not supported');
            return;
        }

        try {
            const quicUrl = this._options.quicUrl
                ? toConfiguredWebTransportUrl(this._options.quicUrl, this._url)
                : toWebTransportUrl(this._url, this._options.quicPath);
            const webTransportOptions = {};
            const serverCertificateHashes =
                this._options.serverCertificateHashes ||
                toServerCertificateHashes(this._options.quicServerCertificateHash);
            if (serverCertificateHashes) {
                webTransportOptions.serverCertificateHashes = serverCertificateHashes;
            }

            this._transport = new runtime.WebTransport(quicUrl, webTransportOptions);
            await this._transport.ready;

            const stream = await this._transport.createBidirectionalStream();
            this._reader = stream.readable.getReader();
            this._writer = stream.writable.getWriter();

            this.safeEmit('open');
            this._startHeartbeat();
            void this._readLoop();

            this._transport.closed
                .then(() => this._handleClose())
                .catch((error) => this._handleClose(error));
        } catch (error) {
            logger.warn('WebTransport connect failed: %o', error);
            if (this._options.fallbackToWebSocket !== false) {
                this._fallbackToWebSocket(error);
                return;
            }
            this.safeEmit('error', error);
            this.safeEmit('failed', 1);
            await this._retry();
        }
    }

    async _readLoop() {
        while (!this._closed && this._reader) {
            const { value, done } = await this._reader.read();
            if (done) break;
            if (!value) continue;
            this._handleChunk(value);
        }
    }

    _handleChunk(value) {
        this._buffer += this._decoder.decode(value, { stream: true });

        let index = this._buffer.indexOf('\n');
        while (index >= 0) {
            const raw = this._buffer.slice(0, index).trim();
            this._buffer = this._buffer.slice(index + 1);
            if (raw) this._handleRawMessage(raw);
            index = this._buffer.indexOf('\n');
        }
    }

    _handleRawMessage(raw) {
        const message = Message.parse(raw);
        if (!message) {
            this.safeEmit('errorParseMessage');
            return;
        }
        if (message.notification && message.method === 'pong') {
            this._missedPongs = 0;
        }
        this.safeEmit('message', message);
    }

    _handleClose(error) {
        if (this._closed) return;
        if (error) logger.warn('WebTransport closed with error: %o', error);
        this.safeEmit('disconnected');
        void this._retry();
    }

    async _retry() {
        this._closeWebTransport();
        if (this._closed) return;
        if (Date.now() - this._connectStartedAt > this._maxRetryTime) {
            this._closed = true;
            this.safeEmit('connectionTimeOut');
            return;
        }
        await wait(1000);
        await this._runWebTransport();
    }

    _closeWebTransport() {
        this._clearHeartbeat();
        try { this._reader?.cancel(); } catch {}
        try { this._writer?.close(); } catch {}
        try { this._transport?.close(); } catch {}
        this._reader = null;
        this._writer = null;
        this._transport = null;
        this._buffer = '';
    }

    _startHeartbeat() {
        this._clearHeartbeat();
        this._missedPongs = 0;
        this._sendHeartbeat();
        this._heartbeatTimer = setInterval(() => this._sendHeartbeat(), HEARTBEAT_INTERVAL);
        this._heartbeatCheckTimer = setInterval(() => {
            if (this._missedPongs > HEARTBEAT_MAX_MISSED) {
                logger.warn('WebTransport heartbeat timeout, reconnecting');
                this._handleClose(new Error('heartbeat timeout'));
            }
        }, HEARTBEAT_CHECK_INTERVAL);
    }

    _sendHeartbeat() {
        if (this._closed || !this._writer) return;
        this._missedPongs += 1;
        const payload = `${JSON.stringify(Message.createNotification('ping', {}))}\n`;
        this._writer.write(this._encoder.encode(payload)).catch((error) => {
            logger.warn('WebTransport heartbeat send failed: %o', error);
            this._handleClose(error);
        });
    }

    _clearHeartbeat() {
        clearInterval(this._heartbeatTimer);
        clearInterval(this._heartbeatCheckTimer);
        this._heartbeatTimer = null;
        this._heartbeatCheckTimer = null;
    }

    _fallbackToWebSocket(reason) {
        logger.warn('fallback to WebSocket: %o', reason);
        this._fallbackTransport = new WebSocketTransport(this._url, this._options);
        this._fallbackTransport.on('open', (...args) => this.safeEmit('open', ...args));
        this._fallbackTransport.on('disconnected', (...args) => this.safeEmit('disconnected', ...args));
        this._fallbackTransport.on('failed', (...args) => this.safeEmit('failed', ...args));
        this._fallbackTransport.on('error', (...args) => this.safeEmit('error', ...args));
        this._fallbackTransport.on('close', (...args) => this.safeEmit('close', ...args));
        this._fallbackTransport.on('connectionTimeOut', (...args) => this.safeEmit('connectionTimeOut', ...args));
        this._fallbackTransport.on('message', (...args) => this.safeEmit('message', ...args));
    }
}
